import { useEffect, useState } from "react";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { DatePicker } from "@/components/ui/DatePicker";
import { TimeField } from "@/components/ui/TimeField";
import { Select } from "@/components/ui/Select";
import { Icon } from "@/components/ui/Icon";
import {
  EVENT_MEMO_MAX,
  EVENT_TITLE_MAX,
  buildIcs,
  googleCalendarUrl,
  icsFileName,
  normalizeEventInput,
  type PersonalEvent,
  type PersonalEventInput,
  type PersonalEventKind,
} from "@/domain/personalEvent";
import {
  GOOGLE_MEMO_MAX,
  googleEventAsPersonal,
  googleReadOnlyReason,
  type GoogleCalendarInfo,
  type GoogleEvent,
} from "@/domain/googleCalendar";
import { DEFAULT_CALENDAR, DEFAULT_CALENDAR_ID, calendarColorCss, storeKeyErp, storeKeyGoogle, type UserCalendar } from "@/domain/calendar";

/**
 * 개인 일정 추가·수정 — 평면 폼 모달(.modal-backdrop > .modal-panel, 본보기 pages/Cost/ListInfoModal.tsx).
 * 베이스 .modal(사이드탭 전용 720px 고정)은 쓰지 않는다.
 *
 * 수정 모드에서만: 왼쪽 아래 [삭제] + "외부 캘린더" 줄(Google 캘린더 링크 · .ics 내려받기).
 * 외부 캘린더는 **저장된 내용** 기준(편집 중인 값이 아니라) — 저장 후 다시 열어 내보낸다.
 *
 * 2차(2026-10-19): 종류 [일정 | 할 일]. 할 일은 날짜만(시간 없음 = 종일) + 수정 모드에서 "완료" 체크.
 *   외부 캘린더 내보내기는 일정에만. 종류는 새로 만들 때만 고른다(수정 중 바꾸면 완료 상태가 애매해져서).
 *
 * 3차(2026-10-23, 설계 docs/design/11_Google캘린더_연결설계.md): Google 캘린더 연결 시
 *   - 새 일정: "저장 위치" [ERP 개인 일정 | 쓰기 가능한 Google 캘린더…]. 할 일은 ERP 에만.
 *   - Google 일정 수정(googleEvent): 같은 폼, 메모 한도 2000자, 내보내기 줄 대신 "Google 캘린더에서 열기".
 *     읽기 전용(남이 만든 일정·읽기 권한 캘린더·여러 날 일정)이면 입력을 잠그고 사유를 보여 준다.
 *
 * 4차(2026-10-29, 캘린더 묶음): "저장 위치" = 내 캘린더(기본 + 내가 만든 것) 그룹 + Google(가져온·쓰기 가능) 그룹.
 *   ERP 일정은 수정 중에도 내 캘린더 사이에서 옮길 수 있다(Google 으로 옮기기는 없음). 할 일은 내 캘린더만.
 */

/** 저장 위치 — 내 캘린더 하나 또는 Google 캘린더 하나 */
export type EventSaveTarget =
  | { store: "erp"; calendarId: string }
  | { store: "google"; calendarId: string; memoChanged: boolean };


interface Props {
  /** 없으면 새 일정 (googleEvent 가 있으면 무시) */
  event?: PersonalEvent;
  /** Google 일정 수정 */
  googleEvent?: GoogleEvent;
  /** Google 일정이 속한 캘린더(이름·색 표시) */
  googleCalendar?: GoogleCalendarInfo;
  /** 새 일정 기본 날짜 */
  defaultDate: string;
  /** 새로 만들 때 기본 종류 (기본 일정) */
  defaultKind?: PersonalEventKind;
  /** 내 캘린더(기본 포함). 없으면 기본만 */
  userCalendars?: UserCalendar[];
  /** 새 일정 저장 위치 후보 = 가져온 Google 캘린더 중 쓰기 가능한 것 */
  writableCalendars?: GoogleCalendarInfo[];
  /** 새 일정 기본 저장 위치 (storeKeyErp / storeKeyGoogle 값) */
  defaultStore?: string;
  holidayLabel?: (ymd: string) => string | null;
  onSave: (input: PersonalEventInput, target: EventSaveTarget) => Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
}

function downloadIcs(ev: PersonalEvent) {
  const blob = new Blob([buildIcs(ev)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = icsFileName(ev);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const KIND_LABEL: Record<PersonalEventKind, string> = { event: "일정", todo: "할 일" };
const KIND_OPTIONS: PersonalEventKind[] = ["event", "todo"];
const STORE_DEFAULT = storeKeyErp(DEFAULT_CALENDAR_ID);

export function PersonalEventModal({
  event: erpEvent,
  googleEvent,
  googleCalendar,
  defaultDate,
  defaultKind = "event",
  userCalendars = [DEFAULT_CALENDAR],
  writableCalendars = [],
  defaultStore = STORE_DEFAULT,
  holidayLabel,
  onSave,
  onDelete,
  onClose,
}: Props) {
  const event = googleEvent ? googleEventAsPersonal(googleEvent) : erpEvent;
  const isGoogle = !!googleEvent;
  const readOnly = googleEvent ? googleReadOnlyReason(googleEvent) : null;

  const [kind, setKind] = useState<PersonalEventKind>(event?.kind ?? defaultKind);
  const [done, setDone] = useState(event?.done ?? false);
  const isTodo = kind === "todo";
  const [title, setTitle] = useState(event?.title ?? "");
  const [date, setDate] = useState(event?.date ?? defaultDate);
  const [allDay, setAllDay] = useState(event ? event.all_day : true);
  const [start, setStart] = useState(event?.start_time ?? "09:00");
  const [end, setEnd] = useState(event?.end_time ?? "");
  const [memo, setMemo] = useState(event?.memo ?? "");
  const [storeRaw, setStore] = useState(() => {
    // 수정 = 그 일정의 캘린더. 새 일정 = 마지막에 쓴 위치(아직 있으면), 아니면 기본
    if (erpEvent) return storeKeyErp(erpEvent.calendar_id && userCalendars.some((c) => c.id === erpEvent.calendar_id) ? erpEvent.calendar_id : DEFAULT_CALENDAR_ID);
    const okErp = userCalendars.some((c) => storeKeyErp(c.id) === defaultStore);
    const okGoogle = writableCalendars.some((c) => storeKeyGoogle(c.id) === defaultStore);
    return okErp || okGoogle ? defaultStore : STORE_DEFAULT;
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const dismiss = useBackdropDismiss(onClose);

  // 위치 선택: Google 일정 수정은 고정. 할 일·ERP 수정은 내 캘린더만, 새 일정은 내 캘린더 + Google
  const googleOptions = !event && !isTodo ? writableCalendars : [];
  const showStore = !isGoogle && (userCalendars.length > 1 || googleOptions.length > 0);
  // 할 일로 바꾸면 Google 위치는 못 쓴다 → 기본으로 본다(파생값, 상태는 그대로)
  const store = isTodo && storeRaw.startsWith("google:") ? STORE_DEFAULT : storeRaw;
  const toGoogle = isGoogle || (showStore && store.startsWith("google:"));
  const memoMax = toGoogle ? GOOGLE_MEMO_MAX : EVENT_MEMO_MAX;

  // Esc 닫기 — 확인창 등 다른 팝업이 먼저 처리한 Esc 는 건드리지 않는다
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    if (readOnly) return;
    const timed = !isTodo && !allDay;
    const r = normalizeEventInput(
      {
        kind, done: isTodo && done,
        title, date, all_day: !timed,
        start_time: timed ? start : undefined,
        end_time: timed ? end || undefined : undefined,
        memo,
      },
      memoMax,
    );
    if (!r.ok) { setErr(r.error); return; }
    const target: EventSaveTarget = googleEvent
      ? { store: "google", calendarId: googleEvent.calendar_id, memoChanged: memo.trim() !== (event?.memo ?? "") }
      : toGoogle
        ? { store: "google", calendarId: store.slice("google:".length), memoChanged: true }
        : { store: "erp", calendarId: store.startsWith("erp:") ? store.slice("erp:".length) : DEFAULT_CALENDAR_ID };
    if (target.store === "erp") r.value.calendar_id = target.calendarId;
    setErr("");
    setSaving(true);
    try {
      await onSave(r.value, target);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "저장하지 못했습니다.");
      setSaving(false);
    }
  }

  const locked = saving || !!readOnly;
  const heading = isGoogle ? (readOnly ? "Google 일정" : "Google 일정 수정") : `${KIND_LABEL[kind]} ${event ? "수정" : "추가"}`;

  return (
    <div className="modal-backdrop" {...dismiss}>
      <div className="modal-panel my-ev-modal" role="dialog" aria-modal="true" aria-labelledby="my-ev-title">
        <div className="modal-head">
          <h3 id="my-ev-title">{heading}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
            <Icon name="close" size={16} />
          </button>
        </div>

        <form
          className="my-ev-body"
          onSubmit={(e) => { e.preventDefault(); void save(); }}
        >
          {isGoogle && (
            <div className="my-ev-gcal">
              <i className="my-gcal-dot" style={{ background: googleCalendar?.color || undefined }} aria-hidden="true" />
              <span className="my-ev-gcal-name">{googleCalendar?.name ?? "Google 캘린더"}</span>
              {googleEvent?.recurring && <span className="my-tag">반복 · 이 회차만</span>}
            </div>
          )}
          {readOnly && <div className="my-local-note">{readOnly}</div>}

          {!event && (
            <div className="my-ev-field">
              <span>종류</span>
              <div className="my-seg" role="group" aria-label="종류">
                {KIND_OPTIONS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`my-seg-btn${kind === k ? " is-on" : ""}`}
                    aria-pressed={kind === k}
                    onClick={() => setKind(k)}
                  >
                    {KIND_LABEL[k]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showStore && (
            <div className="my-ev-field">
              <span>저장 위치</span>
              <Select
                value={store}
                onChange={setStore}
                ariaLabel="저장 위치"
                groups={[
                  { label: "내 캘린더 (나만 봄)", options: userCalendars.map((c) => ({ value: storeKeyErp(c.id), label: c.name })) },
                  ...(googleOptions.length
                    ? [{ label: "Google 캘린더", options: googleOptions.map((c) => ({ value: storeKeyGoogle(c.id), label: c.name })) }]
                    : []),
                ]}
              />
              {store.startsWith("erp:") && (
                <span className="my-ev-storehint">
                  <i className="my-cl-dot" style={{ background: calendarColorCss(userCalendars.find((c) => storeKeyErp(c.id) === store)?.color) }} aria-hidden="true" />
                  {event ? "캘린더를 바꾸면 그 캘린더로 옮겨집니다" : "내 캘린더 일정은 나만 봅니다"}
                </span>
              )}
            </div>
          )}

          <label className="my-ev-field">
            <span>제목</span>
            <input
              className="my-ev-inp"
              value={title}
              maxLength={EVENT_TITLE_MAX}
              placeholder={isTodo ? "예: 견적서 보내기" : "예: 거래처 미팅"}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!!readOnly}
              autoFocus={!readOnly}
            />
          </label>

          <div className="my-ev-field">
            <span>날짜</span>
            <DatePicker value={date} onChange={setDate} ariaLabel="날짜" holidayLabel={holidayLabel} fullWidth disabled={!!readOnly} />
          </div>

          {isTodo ? (
            event && (
              <label className="my-ev-check">
                <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} />
                완료
              </label>
            )
          ) : (
          <div className="my-ev-field">
            <span className="my-ev-timehd">
              시간
              <label className="my-ev-check">
                <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} disabled={!!readOnly} />
                종일
              </label>
            </span>
            {!allDay && (
              <div className="my-ev-times">
                <TimeField value={start} onChange={setStart} ariaLabel="시작 시각" disabled={!!readOnly} />
                <span className="my-ev-tilde" aria-hidden="true">~</span>
                <TimeField value={end} onChange={setEnd} ariaLabel="끝 시각 (선택)" disabled={!!readOnly} />
                {end && !readOnly && (
                  <button type="button" className="btn-tiny" onClick={() => setEnd("")}>끝 시각 지우기</button>
                )}
              </div>
            )}
          </div>
          )}

          <label className="my-ev-field">
            <span>메모 <em className="my-ev-opt">선택</em></span>
            <textarea
              className="my-ev-inp my-ev-memo"
              value={memo}
              maxLength={memoMax}
              rows={3}
              onChange={(e) => setMemo(e.target.value)}
              disabled={!!readOnly}
            />
          </label>

          {isGoogle && googleEvent?.location && (
            <div className="my-ev-field">
              <span>장소</span>
              <div className="my-ev-static">{googleEvent.location}</div>
            </div>
          )}

          {isGoogle && googleEvent?.html_link && (
            <div className="my-ev-export">
              <a className="btn-tiny" href={googleEvent.html_link} target="_blank" rel="noopener noreferrer">
                <Icon name="calendar" size={14} />
                Google 캘린더에서 열기
              </a>
            </div>
          )}

          {!isGoogle && event && event.kind === "event" && (
            <div className="my-ev-export">
              <span className="my-ev-export-lbl">외부 캘린더</span>
              <a
                className="btn-tiny"
                href={googleCalendarUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="calendar" size={14} />
                Google 캘린더에 추가
              </a>
              <button type="button" className="btn-tiny" onClick={() => downloadIcs(event)}>
                .ics 내려받기
              </button>
            </div>
          )}

          {err && <div className="my-ev-err" role="alert">{err}</div>}
          {/* Enter 제출용 — 실제 버튼은 아래 footer */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>

        <div className="my-ev-footer">
          {event && onDelete && !readOnly && (
            <button type="button" className="btn-ghost is-danger" onClick={onDelete} disabled={saving}>
              삭제
            </button>
          )}
          <span className="my-ev-spacer" />
          {readOnly ? (
            <button type="button" className="btn-ghost" onClick={onClose}>닫기</button>
          ) : (
            <>
              <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>취소</button>
              <button type="button" className="btn-primary" onClick={() => void save()} disabled={locked}>
                {saving ? (toGoogle ? "Google 에 저장 중…" : "저장 중…") : "저장"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
